import { AUTH_METHODS, ROLES, STAFF_ROLES, type AuthMethod, type Role } from './enums';

export const PERMISSIONS = [
  'households.read',
  'households.write',
  'decisions.write',
  'scores.override',
  'scores.feedback',
  'scores.rescore',
  'recommendations.write',
  'recommendations.import',
  'activities.write',
  'duplicates.resolve',
  'ingestion.read',
  'audit.read',
  'settings.read',
  'settings.write',
  'users.manage',
  'referrals.configure',
  'referrals.review',
  'referrals.own',
  'notifications.manage',
  'access_requests.manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const TRUSTEE_PERMISSIONS: readonly Permission[] = ['referrals.own'];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  super_admin: PERMISSIONS.filter((p) => !TRUSTEE_PERMISSIONS.includes(p)),
  admissions: [
    'households.read',
    'households.write',
    'decisions.write',
    'scores.feedback',
    'scores.rescore',
    'recommendations.write',
    'recommendations.import',
    'activities.write',
    'duplicates.resolve',
    'ingestion.read',
    'referrals.review',
  ],
  trustee: TRUSTEE_PERMISSIONS,
};

export function hasPermission(roles: readonly string[], permission: Permission): boolean {
  return roles.some((role) => ROLE_PERMISSIONS[role as Role]?.includes(permission) ?? false);
}

export function permissionsFor(roles: readonly string[]): Permission[] {
  return PERMISSIONS.filter((p) => hasPermission(roles, p));
}

export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}

export function parseRoles(values: readonly string[]): { roles: Role[]; unknown: string[] } {
  const known = new Set(values.filter(isRole));
  return { roles: ROLES.filter((r) => known.has(r)), unknown: values.filter((v) => !isRole(v)) };
}

export function hasStaffRole(roles: readonly string[]): boolean {
  return roles.some((r) => STAFF_ROLES.includes(r as Role));
}

export function defaultAuthMethods(roles: readonly string[]): AuthMethod[] {
  return hasStaffRole(roles) ? ['google'] : ['google', 'email_code'];
}

export function parseAuthMethods(values: readonly string[]): AuthMethod[] {
  return AUTH_METHODS.filter((m) => values.includes(m));
}
